/**
 * Preview a template for a chosen member or the fictional sample member
 * (POST /api/template-variables/preview). Never sends, never saves.
 *
 * Shows the rendered subject and body, every variable that could not be
 * filled with the reason, and — for `input` variables — fields to try values.
 */

import React, { useCallback, useEffect, useState } from 'react';
import { Eye, Loader2 } from 'lucide-react';
import { useAppTranslation } from '@/contexts/TranslationContext';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { MemberPicker, type PickedMember } from '@/components/behavioral-assessment/MemberPicker';
import {
  cleanInputs,
  parseApiError,
  templateVariablesApi,
  type CheckSurface,
  type PreviewContext,
  type PreviewResponse,
} from './api';
import { UnresolvedList } from './TemplateCheck';
import { VariableInputFields } from './VariableInputFields';

export interface TemplatePreviewProps {
  subject?: string | null;
  body: string;
  appliesTo: CheckSurface;
  /** Render the body as HTML (email / letter) inside a sandboxed frame. */
  html?: boolean;
  context?: PreviewContext;
  /** Pre-select a member (e.g. the member a composer is sending to). */
  member?: PickedMember | null;
  /** Values already entered for input variables. */
  variableInputs?: Record<string, string>;
}

interface TemplatePreviewButtonProps extends TemplatePreviewProps {
  disabled?: boolean;
  label?: string;
  size?: 'sm' | 'default';
}

/** A "Preview" button that opens the preview dialog. */
export function TemplatePreviewButton({ disabled, label, size = 'sm', ...props }: TemplatePreviewButtonProps) {
  const { t } = useAppTranslation();
  const [open, setOpen] = useState(false);
  return (
    <>
      <Button
        type="button"
        variant="outline"
        size={size}
        disabled={disabled || !(props.body || '').trim()}
        onClick={() => setOpen(true)}
      >
        <Eye className="mr-1.5 h-4 w-4" aria-hidden />
        {label ?? t('templateVariables.preview.button', 'Preview')}
      </Button>
      {open && <TemplatePreviewDialog {...props} open={open} onClose={() => setOpen(false)} />}
    </>
  );
}

export function TemplatePreviewDialog({
  open,
  onClose,
  subject,
  body,
  appliesTo,
  html,
  context,
  member: initialMember = null,
  variableInputs,
}: TemplatePreviewProps & { open: boolean; onClose: () => void }) {
  const { t } = useAppTranslation();
  const [mode, setMode] = useState<'sample' | 'member'>(initialMember ? 'member' : 'sample');
  const [member, setMember] = useState<PickedMember | null>(initialMember);
  const [inputs, setInputs] = useState<Record<string, string>>(variableInputs ?? {});
  const [result, setResult] = useState<PreviewResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const run = useCallback(async () => {
    if (mode === 'member' && !member) {
      setResult(null);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const data = await templateVariablesApi.preview({
        subject: subject ?? undefined,
        body,
        applies_to: appliesTo,
        member_id: mode === 'member' ? member?.id ?? null : null,
        context,
        variable_inputs: cleanInputs(inputs),
      });
      setResult(data);
    } catch (err) {
      const e = parseApiError(err);
      setResult(null);
      setError(
        e.code === 'member_not_found'
          ? t('templateVariables.preview.memberNotFound', 'That member is not an active member of this organization.')
          : e.message || t('templateVariables.preview.failed', 'Could not render the preview.'),
      );
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode, member, inputs, subject, body, appliesTo, JSON.stringify(context ?? {})]);

  useEffect(() => {
    if (!open) return;
    const h = window.setTimeout(() => void run(), 250);
    return () => window.clearTimeout(h);
  }, [open, run]);

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>{t('templateVariables.preview.title', 'Preview')}</DialogTitle>
          <DialogDescription>
            {t('templateVariables.preview.description', 'See this template filled in. Nothing is sent or saved.')}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="flex flex-wrap items-center gap-4 text-sm text-ink">
            <label className="inline-flex items-center gap-2">
              <input type="radio" name="tv-preview-mode" checked={mode === 'sample'} onChange={() => setMode('sample')} />
              {t('templateVariables.preview.sampleMember', 'Sample member')}
            </label>
            <label className="inline-flex items-center gap-2">
              <input type="radio" name="tv-preview-mode" checked={mode === 'member'} onChange={() => setMode('member')} />
              {t('templateVariables.preview.chooseMember', 'A member')}
            </label>
          </div>
          {mode === 'member' && (
            <MemberPicker
              id="tv-preview-member"
              value={member}
              onChange={setMember}
              label={t('templateVariables.preview.member', 'Member')}
              placeholder={t('templateVariables.preview.searchMember', 'Search by name…')}
            />
          )}

          {result && result.required_inputs.length > 0 && (
            <VariableInputFields inputs={result.required_inputs} values={inputs} onChange={setInputs} idPrefix="tv-preview-input" />
          )}

          {loading && (
            <p className="flex items-center gap-2 text-sm text-ink-500">
              <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
              {t('templateVariables.preview.rendering', 'Rendering…')}
            </p>
          )}
          {error && (
            <p role="alert" className="rounded-md bg-danger-soft px-3 py-2 text-sm text-danger">
              {error}
            </p>
          )}

          {result && !loading && (
            <div className="space-y-3" data-testid="template-preview-result">
              {result.sample_member && (
                <p className="text-xs text-ink-500">
                  {t('templateVariables.preview.sampleNote', 'Shown for a fictional sample member.')}
                </p>
              )}
              {result.subject !== null && result.subject !== undefined && (
                <div>
                  <p className="text-[11px] font-semibold uppercase tracking-wide text-ink-500">
                    {t('templateVariables.preview.subject', 'Subject')}
                  </p>
                  <p className="text-sm text-ink">{result.subject}</p>
                </div>
              )}
              <div>
                <p className="text-[11px] font-semibold uppercase tracking-wide text-ink-500">
                  {t('templateVariables.preview.body', 'Message')}
                </p>
                {html ? (
                  <iframe
                    title={t('templateVariables.preview.body', 'Message')}
                    sandbox=""
                    srcDoc={result.body}
                    className="h-72 w-full rounded-md border border-v2border bg-white"
                  />
                ) : (
                  <div className="whitespace-pre-wrap rounded-md border border-v2border bg-paper-2 p-3 text-sm text-ink">
                    {result.body}
                  </div>
                )}
              </div>
              {result.unresolved.length > 0 ? (
                <div role="alert" className="rounded-md border border-danger bg-danger-soft px-3 py-2">
                  <p className="text-xs font-semibold text-danger">
                    {t('templateVariables.preview.unresolvedTitle', 'Not filled — a send to this member would be refused')}
                  </p>
                  <UnresolvedList items={result.unresolved} tone="error" />
                </div>
              ) : (
                <p className="text-xs text-success-ink">{t('templateVariables.preview.allFilled', 'Every variable is filled.')}</p>
              )}
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
